'use client';
import { X } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import Button from '../common/Button';

const modalStack: Array<() => void> = [];

interface Props {
  children: React.ReactNode;
  style?: React.CSSProperties;
  closeHandler?: () => void;
  maxWidth?: number;
  /** Set false when a stray click outside should NOT dismiss — a prompt the
   *  user has to answer, rather than a panel they are browsing. The X and any
   *  in-content buttons still close it. */
  dismissOnBackdrop?: boolean;
}

const Modal: FC<Props> = ({
  children,
  style = { marginLeft: '20px', marginRight: '20px' },
  closeHandler,
  maxWidth = 700,
  dismissOnBackdrop = true
}) => {
  // Ignore synthetic mousedowns that bubble from portaled children (e.g. nested modals).
  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const overlay = e.currentTarget;
    if (!overlay.contains(e.target as Node)) return;
    e.stopPropagation();
    if (!dismissOnBackdrop) return;
    closeHandler?.();
  };

  // Stop React-tree bubbling: modal is portaled but synthetic events still
  // reach the parent that rendered <Modal /> (e.g. PostItem's click-to-nav).
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // Lock scroll on AppLayout container if present, else body. Compensate
  // scrollbar width so layout doesn't shift.
  useEffect(() => {
    const scrollRoot =
      document.querySelector<HTMLElement>('[data-scroll-lock-root]') ??
      document.body;
    const scrollbarWidth =
      scrollRoot === document.body
        ? window.innerWidth - document.documentElement.clientWidth
        : scrollRoot.offsetWidth - scrollRoot.clientWidth;
    const prevOverflow = scrollRoot.style.overflow;
    const prevPaddingRight = scrollRoot.style.paddingRight;
    scrollRoot.style.overflow = 'hidden';
    scrollRoot.style.paddingRight =
      scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    return () => {
      scrollRoot.style.overflow = prevOverflow;
      scrollRoot.style.paddingRight = prevPaddingRight;
    };
  }, []);

  // Portals can't render during SSR (no document), and rendering one on the
  // first client render would mismatch the server's null. Gate on a mount flag
  // so the server and the first client render agree (null), then portal after
  // hydration. Without this, a modal that's open on initial load (e.g. the
  // onboarding modal) hydration-mismatches.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return null;
  }

  // Portal to body: Safari's position:fixed breaks inside nested containers.
  return createPortal(
    <div
      className="animate-fadeIn fixed inset-0 z-modal h-screen w-screen bg-black/50"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        width: '100vw',
        height: '100vh'
      }}
      onMouseDown={handleOverlayMouseDown}
      onClick={stop}
      onMouseUp={stop}
    >
      <div className="flex h-screen items-center justify-center">
        <div
          data-testid="modal"
          role="dialog"
          aria-modal="true"
          style={{ ...style, maxWidth }}
          className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-ds-border-standard bg-background-secondary shadow-2xl transition-[max-width] duration-300 ease-out"
          onClick={stop}
          onMouseDown={stop}
          onPointerDown={stop}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export const ModalHeader: FC<{
  children?: React.ReactNode;
  closeHandler?: () => void;
  headerElement?: React.ReactNode;
  flex?: boolean;
  /** Replaces the X when closing means something other than "stop this" —
   *  a deploy, say, which keeps running on the runner after the modal goes. */
  closeIcon?: React.ReactNode;
  /** Accessible name for that control. Defaults to "Close". */
  closeLabel?: string;
}> = ({
  children,
  closeHandler,
  headerElement,
  flex = true,
  closeIcon,
  closeLabel
}) => {
  useEffect(() => {
    if (!closeHandler) return;
    modalStack.push(closeHandler);
    const closeModal = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        modalStack[modalStack.length - 1] === closeHandler
      ) {
        closeHandler();
      }
    };
    document.addEventListener('keydown', closeModal);
    return () => {
      document.removeEventListener('keydown', closeModal);
      const idx = modalStack.indexOf(closeHandler);
      if (idx !== -1) modalStack.splice(idx, 1);
    };
  }, [closeHandler]);

  return (
    <div
      className={` ${flex ? 'flex' : ''} modal-header shrink-0 items-center justify-between border-b border-solid border-ds-border-subtle p-4 text-lg font-semibold`}
    >
      {headerElement ? headerElement : children}
      {closeHandler && (
        <Button
          variant="SECONDARY"
          onClick={() => closeHandler()}
          ariaLabel={closeLabel ?? 'Close'}
          icon={closeIcon ?? <X size={20} />}
        />
      )}
    </div>
  );
};

export const ModalContent: FC<{
  children: React.ReactNode;
  banner?: React.ReactNode;
  // Opt-in for modals whose body content grows/shrinks (e.g. live search
  // results). Holds a stable min-height ON THE SCROLL CONTAINER so the leftover
  // space is slack (no scrollbar) instead of forcing a tall child that
  // overflows — and the modal doesn't bounce as content changes. Off by
  // default so other modals stay sized to their content. Clamped to
  // 90vh-minus-chrome (20rem ≈ header + a tall footer) so header/footer stay
  // visible on short screens.
  fillHeight?: boolean;
  // Stronger than fillHeight: pins the scroller to ONE height, so a modal whose
  // body swaps between screens (a navigation stack) never resizes as you move
  // through it. fillHeight only sets a floor, which still lets a tall screen
  // grow the modal and makes the next one snap back down.
  //
  // 'compact' pins a shorter one. Pick it when the screens people actually see
  // are short and the tall one is the exception: the full height then leaves a
  // block of dead space under every normal screen, and the exception can
  // scroll instead.
  lockHeight?: boolean | 'compact';
}> = ({ children, banner, fillHeight, lockHeight }) => (
  <>
    {banner && banner}
    {/* min-h-0 lets this flex child shrink inside the shell's 90vh clamp, making
        it the modal's ONLY scroller — the shell is overflow-hidden. */}
    <div
      className={`modal-content min-h-0 w-full overflow-y-auto overscroll-y-contain p-4 ${
        lockHeight === 'compact'
          ? 'h-[min(24rem,calc(90vh-11rem))]'
          : lockHeight
            ? 'h-[min(36rem,calc(90vh-11rem))]'
            : fillHeight
              ? 'min-h-[min(32rem,calc(90vh-20rem))]'
              : ''
      }`}
    >
      {children}
    </div>
  </>
);

export const ModalFooter: FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <div className="shrink-0 space-x-2 border-t border-solid border-ds-border-subtle p-3 text-center md:text-end laptop:text-end">
    {children}
  </div>
);

export default Modal;
